import AssessmentIcon from '@material-ui/icons/Assessment';
import React, { useEffect, useRef } from 'react';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';

const ChangeOrderImpactAssessmentCardFields = [
  {
    name: 'productDesignAssessment',
    label: 'Product Design Assessment',
    description:
      'Product Owner describes impact on Design Requirements, Specifications, Verification & Validation activities'
  },
  {
    name: 'productRiskAssessment',
    label: 'Product Risk Assessment',
    description:
      'Product Owner describes  impact on product risk, does this change require a risk assessment? SRA, Software, Cybersecurity.'
  },
  {
    name: 'affectedDocuments',
    label: 'Affected Documents',
    description:
      'Product Owner describes all affected documents, eg BOM, DRW, SCH, SW, Labels, IFU, V&V test plans'
  },
  {
    name: 'regulatoryAssessment',
    label: 'Regulatory Assessment',
    description:
      'Head of Regulatory Affairs to describe if change affects devices, components, labeling, or software that have received regulatory (FDA, EU NB, etc.) clearance/approval or are currently under review?'
  },
  {
    name: 'qualityAssessment',
    label: 'Quality Assessment',
    description:
      'Quality Assurance Manager to describe if change affects,  QMS processes, training etc'
  },
  {
    name: 'labellingAssessment',
    label: 'Labelling Assessment',
    description:
      'Quality Assurance Manager to describe if change affects,  IFU, labelling, user guides etc'
  },
  {
    name: 'manufacturingAssessment',
    label: 'Manufacturing Assessment',
    description:
      'Operations Manager to deceive if change affects,  Manufacturing and QA processes, fixtures, tools, PFMEA, process validations, training etc'
  },
  {
    name: 'supplierAssessment',
    label: 'Supplier Assessment',
    description:
      'Operations Manager to describe if change affects,  Supply chain, WIP,  inventory, PO commitments, new suppliers, etc'
  },
  {
    name: 'clinical',
    label: 'Clinical',
    description:
      'Clinical to describe if change affects clinical function, performance etc'
  }
];

export interface ChangeOrderImpactAssessmentCardProps
  extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

export const ChangeOrderImpactAssessmentCard: React.FC<
  ChangeOrderImpactAssessmentCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    productDesignAssessment: changeOrder.productDesignAssessment || '',
    productRiskAssessment: changeOrder.productRiskAssessment || '',
    affectedDocuments: changeOrder.affectedDocuments || '',
    regulatoryAssessment: changeOrder.regulatoryAssessment || '',
    qualityAssessment: changeOrder.qualityAssessment || '',
    labellingAssessment: changeOrder.labellingAssessment || '',
    manufacturingAssessment: changeOrder.manufacturingAssessment || '',
    supplierAssessment: changeOrder.supplierAssessment || '',
    clinical: changeOrder.clinical || ''
  };
  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [changeOrder, onChange, updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <AssessmentIcon />
          Impact Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {ChangeOrderImpactAssessmentCardFields.map((field, index) => (
              <InlineHtml
                key={index}
                name={field.name}
                label={field.label}
                disabled={isFieldDisabled(field.name)}
                helperText={field.description}
                attachments={changeOrder.task.attachments || []}
                onChangeAttachments={attachments => {
                  updateTask({ attachments });
                  if (changeOrder.task) {
                    changeOrder.task.attachments = attachments;
                  }
                  onChange?.(changeOrder);
                }}
              />
            ))}
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
